import React, { useState, useEffect } from 'react';
import { sanitizePlainHtml } from '../lib/sanitize';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Brain,
  Search,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Crown,
  Archive,
  RotateCcw,
  Save,
  Trash2,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Shield,
  Layout,
  Image,
  Target,
  Users,
  AlertTriangle,
  MessageSquare,
  BookMarked,
  CheckCircle,
  TrendingUp,
  Play,
  FileText,
  Languages,
  ScrollText,
  Lightbulb,
  Mic2,
  Copy,
  Check,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PremiumFeature } from './PremiumFeature';
import { useSectionStyles } from '../hooks/useSectionStyles';
import { useAnalysis } from '../hooks/useAnalysis';
import { useLanguageAnalysis } from '../hooks/useLanguageAnalysis';
import type { LanguageAnalysisRecord } from '../hooks/useLanguageAnalysis';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { useTrialReminder } from '../hooks/useTrialReminder';
import { CompactTrialBanner } from './CompactTrialBanner';
import { SaveNotification } from './SaveNotification';
import { ConfirmationModal } from './ConfirmationModal';
import type { SermonAnalysisJSON, LanguageAnalysisJSON, LanguageTermEntry } from '../lib/openai';
import { analyzeSermon, analyzeLanguage } from '../lib/openai';
import { supabase } from '../lib/supabase';
import { WorshipSongAnalysis } from './Analysis/WorshipSongAnalysis';
import { SpeakerVetting } from './Analysis/SpeakerVetting';

type ResearchTool = 'analysis' | 'language' | 'worship-song' | 'speaker-vetting';

interface LocationState {
  analysis?: string | SermonAnalysisJSON;
  languageAnalysis?: LanguageAnalysisJSON;
  sermonId: string;
  sermonTitle: string;
  sermonScripture?: string;
  tool?: ResearchTool;
  timestamp?: number;
}

interface SectionConfig {
  bg: string;
  border: string;
  header: string;
  bullet: string;
  icon: LucideIcon;
  shadow: string;
  label: string;
}

const SECTION_CONFIG: Record<string, SectionConfig> = {
  biblicalExposition: {
    bg: 'bg-gradient-to-br from-blue-50 via-blue-100/30 to-indigo-50',
    border: 'border-blue-300/60',
    header: 'bg-gradient-to-r from-blue-600 to-indigo-600',
    bullet: 'text-blue-600',
    icon: BookOpen,
    shadow: 'shadow-blue-200/50',
    label: 'Biblical Exposition',
  },
  scriptureAccuracy: {
    bg: 'bg-gradient-to-br from-indigo-50 via-purple-100/30 to-violet-50',
    border: 'border-indigo-300/60',
    header: 'bg-gradient-to-r from-indigo-500 to-violet-500',
    bullet: 'text-indigo-600',
    icon: Search,
    shadow: 'shadow-indigo-200/50',
    label: 'Scripture Accuracy',
  },
  theologicalSoundness: {
    bg: 'bg-gradient-to-br from-purple-50 via-violet-100/30 to-fuchsia-50',
    border: 'border-purple-300/60',
    header: 'bg-gradient-to-r from-purple-500 to-purple-700',
    bullet: 'text-purple-600',
    icon: Shield,
    shadow: 'shadow-purple-200/50',
    label: 'Theological Soundness',
  },
  homilecticalStructure: {
    bg: 'bg-gradient-to-br from-teal-50 via-cyan-100/30 to-sky-50',
    border: 'border-teal-300/60',
    header: 'bg-gradient-to-r from-teal-500 to-teal-700',
    bullet: 'text-teal-600',
    icon: Layout,
    shadow: 'shadow-teal-200/50',
    label: 'Homiletical Structure',
  },
  illustrativeContent: {
    bg: 'bg-gradient-to-br from-amber-50 via-yellow-100/30 to-orange-50',
    border: 'border-amber-300/60',
    header: 'bg-gradient-to-r from-amber-500 to-amber-600',
    bullet: 'text-amber-600',
    icon: Image,
    shadow: 'shadow-amber-200/50',
    label: 'Illustrative Content',
  },
  practicalApplication: {
    bg: 'bg-gradient-to-br from-emerald-50 via-green-100/30 to-teal-50',
    border: 'border-emerald-300/60',
    header: 'bg-gradient-to-r from-emerald-500 to-emerald-700',
    bullet: 'text-emerald-600',
    icon: Target,
    shadow: 'shadow-emerald-200/50',
    label: 'Practical Application',
  },
  congregationConnection: {
    bg: 'bg-gradient-to-br from-sky-50 via-blue-100/30 to-cyan-50',
    border: 'border-sky-300/60',
    header: 'bg-gradient-to-r from-sky-500 to-cyan-600',
    bullet: 'text-sky-600',
    icon: Users,
    shadow: 'shadow-sky-200/50',
    label: 'Congregation Connection',
  },
  warnings: {
    bg: 'bg-gradient-to-br from-red-50 via-rose-100/30 to-orange-50',
    border: 'border-red-300/60',
    header: 'bg-gradient-to-r from-red-500 to-rose-600',
    bullet: 'text-red-600',
    icon: AlertTriangle,
    shadow: 'shadow-red-200/50',
    label: 'Warnings',
  },
  pastoralRecommendations: {
    bg: 'bg-gradient-to-br from-slate-50 via-gray-100/30 to-zinc-50',
    border: 'border-slate-300/60',
    header: 'bg-gradient-to-r from-slate-600 to-slate-700',
    bullet: 'text-slate-600',
    icon: MessageSquare,
    shadow: 'shadow-slate-200/50',
    label: 'Pastoral Recommendations',
  },
  recommendedResources: {
    bg: 'bg-gradient-to-br from-orange-50 via-amber-100/30 to-yellow-50',
    border: 'border-orange-300/60',
    header: 'bg-gradient-to-r from-orange-500 to-amber-600',
    bullet: 'text-orange-600',
    icon: BookMarked,
    shadow: 'shadow-orange-200/50',
    label: 'Recommended Resources',
  },
};

// Fallback for unknown section keys
const FALLBACK_CONFIGS: SectionConfig[] = [
  { bg: 'bg-gradient-to-br from-blue-50 to-indigo-50', border: 'border-blue-200/50', header: 'bg-gradient-to-r from-blue-500 to-indigo-500', bullet: 'text-blue-600', icon: Brain, shadow: 'shadow-blue-200/30', label: '' },
  { bg: 'bg-gradient-to-br from-emerald-50 to-teal-50', border: 'border-emerald-200/50', header: 'bg-gradient-to-r from-emerald-500 to-teal-500', bullet: 'text-emerald-600', icon: Brain, shadow: 'shadow-emerald-200/30', label: '' },
];

function getSectionConfig(key: string, index: number): SectionConfig {
  return SECTION_CONFIG[key] ?? { ...FALLBACK_CONFIGS[index % FALLBACK_CONFIGS.length], label: key.replace(/([A-Z])/g, ' $1').trim() };
}

function AnalysisCards({ data }: { data: SermonAnalysisJSON }) {
  const { overallAssessment, ...sections } = data;

  return (
    <div className="space-y-6">
      {/* Overall Assessment hero card */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-xl border border-slate-700/50 overflow-hidden">
        <div className="p-4 md:p-8">
          <div className="flex items-center mb-5">
            <div className="bg-white/10 p-3 rounded-xl mr-4 shrink-0">
              <Brain className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Overall Assessment</h3>
              <p className="text-white/60 text-sm mt-0.5">AI-Powered Sermon Evaluation</p>
            </div>
          </div>

          <p className="text-white/85 leading-relaxed mb-7 text-base">{overallAssessment.summary}</p>

          {/* Score dashboard */}
          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-7">
            {[
              { label: 'Biblical', value: overallAssessment.score.biblical, color: 'text-blue-400' },
              { label: 'Theological', value: overallAssessment.score.theological, color: 'text-amber-400' },
              { label: 'Practical', value: overallAssessment.score.practical, color: 'text-emerald-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white/10 rounded-xl p-2 md:p-4 text-center">
                <div className={`text-2xl md:text-3xl font-bold ${color}`}>
                  {value}<span className="text-base md:text-lg text-white/40">/10</span>
                </div>
                <div className="text-white/60 text-xs md:text-sm mt-1">{label}</div>
              </div>
            ))}
          </div>

          {/* Strengths & improvements */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-emerald-500/20 rounded-xl p-4 border border-emerald-500/30">
              <h4 className="text-emerald-400 text-sm font-semibold mb-3 flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" /> Key Strengths
              </h4>
              <ul className="space-y-2">
                {overallAssessment.strengths.map((s, i) => (
                  <li key={i} className="text-white/80 text-sm leading-relaxed">{s}</li>
                ))}
              </ul>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-4 border border-amber-500/30">
              <h4 className="text-amber-400 text-sm font-semibold mb-3 flex items-center">
                <TrendingUp className="h-4 w-4 mr-2" /> Focus Areas
              </h4>
              <ul className="space-y-2">
                {overallAssessment.improvements.map((s, i) => (
                  <li key={i} className="text-white/80 text-sm leading-relaxed">{s}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Section cards */}
      {Object.entries(sections).map(([key, items], index) => {
        const config = getSectionConfig(key, index);
        const itemsArray = Array.isArray(items) ? items : [];

        // Skip empty warnings section
        if (key === 'warnings' && itemsArray.length === 0) return null;

        const IconComponent = config.icon;

        return (
          <div
            key={key}
            className={`${config.bg} rounded-xl shadow-lg ${config.shadow} border ${config.border} overflow-hidden`}
          >
            <div className={`${config.header} px-4 md:px-8 py-4 md:py-5 relative overflow-hidden`}>
              <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent" />
              <h3 className="text-base md:text-lg font-bold text-white flex items-center relative z-10">
                <div className="bg-white/20 p-2 md:p-2.5 rounded-lg mr-3 backdrop-blur-sm shrink-0">
                  <IconComponent className="h-4 w-4 md:h-5 md:w-5 text-white" />
                </div>
                {config.label || key.replace(/([A-Z])/g, ' $1').trim()}
              </h3>
            </div>
            <div className="p-4 md:p-8">
              {itemsArray.length > 0 ? (
                <ul className="space-y-3">
                  {itemsArray.map((item: string, i: number) => (
                    <li key={i} className="flex items-start group">
                      <div className={`${config.bullet} mr-3 mt-2 flex-shrink-0`}>
                        <div className="w-2 h-2 rounded-full bg-current" />
                      </div>
                      <span className="text-slate-700 leading-relaxed text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-400 italic text-sm">No analysis available for this section.</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Research() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState<ResearchTool>('analysis');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisData, setAnalysisData] = useState<{
    content: string | SermonAnalysisJSON;
    sermonId: string | null;
    sermonTitle: string | null;
  } | null>(null);
  const [languageData, setLanguageData] = useState<LanguageAnalysisJSON | null>(null);
  const [langSermonId, setLangSermonId] = useState('');
  const [langSermonTitle, setLangSermonTitle] = useState<string | null>(null);
  const [langAnalyzing, setLangAnalyzing] = useState(false);
  const [langError, setLangError] = useState<string | null>(null);
  const [langSaved, setLangSaved] = useState(false);
  const [langSaveNotification, setLangSaveNotification] = useState(false);
  const [langDeleteTarget, setLangDeleteTarget] = useState<string | null>(null);
  const [expandedLangAnalyses, setExpandedLangAnalyses] = useState<Set<string>>(new Set());
  const { styles } = useSectionStyles();
  const {
    analyses,
    loading: analysesLoading,
    error: analysesError,
    showArchived,
    setShowArchived,
    saveAnalysis,
    toggleArchive,
    deleteAnalysis
  } = useAnalysis();
  const {
    analyses: langAnalyses,
    saveAnalysis: saveLangAnalysis,
    deleteAnalysis: deleteLangAnalysis,
  } = useLanguageAnalysis();
  const [expandedAnalyses, setExpandedAnalyses] = useState<Set<string>>(new Set());
  const [saveNotification, setSaveNotification] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [showNewAnalysisPanel, setShowNewAnalysisPanel] = useState(false);
  const [sermonList, setSermonList] = useState<{ id: string; title: string; scripture: string | null }[]>([]);
  const [sermonsLoading, setSermonsLoading] = useState(false);
  const [selectedSermonId, setSelectedSermonId] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const { user } = useAuth();
  const { hasFeature } = useSubscription();
  const { showBanner, dismissBanner, daysLeftInTrial } = useTrialReminder();

  useEffect(() => {
    const state = location.state as LocationState;
    if (state?.tool) setActiveTool(state.tool);
    if (state?.analysis) {
      setLoading(true);
      try {
        setAnalysisData({
          content: state.analysis,
          sermonId: state.sermonId || null,
          sermonTitle: state.sermonTitle || null,
        });
      } catch (err) {
        setError('Failed to process analysis data');
      } finally {
        setLoading(false);
      }
    }
    if (state?.languageAnalysis) {
      setLanguageData(state.languageAnalysis);
      setLangSermonTitle(state.sermonTitle || null);
      if (state.sermonId) setLangSermonId(state.sermonId);
      setLangSaved(false);
    }
  }, [location.state]);

  useEffect(() => {
    if ((!showNewAnalysisPanel && activeTool !== 'language') || !user || sermonList.length > 0) return;
    let isMounted = true;
    setSermonsLoading(true);
    supabase
      .from('sermons')
      .select('id, title, scripture')
      .eq('user_id', user.id)
      .eq('archived', false)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (!isMounted) return;
        setSermonList(data ?? []);
        if (data && data.length > 0) setSelectedSermonId(data[0].id);
        setSermonsLoading(false);
      });
    return () => { isMounted = false; };
  }, [showNewAnalysisPanel, activeTool, user, sermonList.length]);

  const handleRunAnalysis = async () => {
    if (!selectedSermonId) return;
    setAnalyzeError(null);
    setAnalyzing(true);
    try {
      const { data: sermon } = await supabase
        .from('sermons')
        .select('title, content, scripture')
        .eq('id', selectedSermonId)
        .single();
      if (!sermon?.content) {
        setAnalyzeError('This sermon has no content to analyze.');
        return;
      }
      const result = await analyzeSermon(sermon.content, sermon.scripture || undefined);
      if (result.success) {
        setShowNewAnalysisPanel(false);
        setAnalysisData({
          content: result.content,
          sermonId: selectedSermonId,
          sermonTitle: sermon.title,
        });
      } else {
        setAnalyzeError(result.error || 'Analysis failed.');
      }
    } catch (err) {
      setAnalyzeError(err instanceof Error ? err.message : 'Analysis failed.');
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedAnalyses(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSaveAnalysis = async () => {
    if (!analysisData) return;
    try {
      setLoading(true);
      setError(null);
      const serialized = typeof analysisData.content === 'string'
        ? analysisData.content
        : JSON.stringify(analysisData.content);
      const savedAnalysis = await saveAnalysis({
        sermon_id: analysisData.sermonId || undefined,
        title: analysisData.sermonTitle || 'Untitled Analysis',
        content: serialized,
      });
      if (savedAnalysis) {
        setExpandedAnalyses(prev => new Set(prev).add(savedAnalysis.id));
      }
      setSaveNotification(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnalysis = (id: string) => {
    setDeleteTarget(id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAnalysis(deleteTarget);
      setExpandedAnalyses(prev => { const n = new Set(prev); n.delete(deleteTarget); return n; });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete analysis');
    } finally {
      setDeleteTarget(null);
    }
  };

  const tools = [
    {
      id: 'analysis' as ResearchTool,
      name: 'Sermon Analysis',
      icon: Brain,
      description: 'AI-powered sermon analysis and feedback',
      category: 'Sermon Tools',
      feature: 'sermon_analysis'
    },
    {
      id: 'language' as ResearchTool,
      name: 'Language Analysis',
      icon: Languages,
      description: 'Hebrew & Greek word studies from the text',
      category: 'Sermon Tools',
      feature: 'sermon_analysis'
    },
    {
      id: 'worship-song' as ResearchTool,
      name: 'Worship Song Analysis',
      icon: ScrollText,
      description: 'Evaluate a song\'s theological faithfulness',
      category: 'Ministry Vetting',
      feature: 'sermon_analysis'
    },
    {
      id: 'speaker-vetting' as ResearchTool,
      name: 'Speaker Vetting',
      icon: Shield,
      description: 'Theological discernment profile for speakers & conferences',
      category: 'Ministry Vetting',
      feature: 'sermon_analysis'
    },
  ];

  const renderHistoryContent = (content: string) => {
    try {
      const parsed = JSON.parse(content) as SermonAnalysisJSON;
      return <AnalysisCards data={parsed} />;
    } catch {
      // Legacy HTML content
      return (
        <div
          className="prose prose-slate max-w-none"
          dangerouslySetInnerHTML={{ __html: sanitizePlainHtml(content) }}
        />
      );
    }
  };

  const renderAnalysisContent = () => {
    return (
      <PremiumFeature feature="sermon_analysis">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-electric" />
          </div>
        ) : error ? (
          <div className="text-center p-8">
            <div className="bg-crimson/10 text-crimson p-4 rounded-lg inline-flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
          </div>
        ) : !analysisData?.content ? (
          <div>
            <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
              <h2 className="text-xl font-semibold text-midnight">Analysis History</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setShowNewAnalysisPanel(v => !v); setAnalyzeError(null); }}
                  className="flex items-center px-4 py-2 rounded-lg bg-electric text-white hover:bg-electric/90 transition-colors"
                >
                  <Play className="h-4 w-4 mr-2" />
                  New Analysis
                </button>
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  className={`flex items-center px-4 py-2 rounded-lg ${
                    showArchived
                      ? 'bg-slate/20 text-slate hover:bg-slate/30'
                      : 'bg-sand/20 text-slate hover:bg-sand/30'
                  }`}
                >
                  {showArchived ? (
                    <><RotateCcw className="h-5 w-5 mr-2" />Show Active</>
                  ) : (
                    <><Archive className="h-5 w-5 mr-2" />Show Archived</>
                  )}
                </button>
              </div>
            </div>

            {showNewAnalysisPanel && (
              <div className="mb-6 p-5 bg-white border border-electric/20 rounded-xl shadow-sm">
                <h3 className="text-sm font-semibold text-midnight mb-3 flex items-center gap-2">
                  <Brain className="h-4 w-4 text-electric" />
                  Select a sermon to analyze
                </h3>
                {sermonsLoading ? (
                  <div className="flex items-center gap-2 text-slate text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading sermons…
                  </div>
                ) : sermonList.length === 0 ? (
                  <p className="text-sm text-slate">No sermons found in your library.</p>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate pointer-events-none" />
                      <select
                        value={selectedSermonId}
                        onChange={e => setSelectedSermonId(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 border border-sand/40 rounded-lg bg-sand/5 text-midnight text-sm focus:outline-none focus:ring-2 focus:ring-electric/30 appearance-none"
                      >
                        {sermonList.map(s => (
                          <option key={s.id} value={s.id}>{s.title || 'Untitled'}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate pointer-events-none" />
                    </div>
                    <button
                      onClick={handleRunAnalysis}
                      disabled={analyzing || !selectedSermonId}
                      className="flex items-center justify-center px-5 py-2.5 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 text-sm font-medium whitespace-nowrap"
                    >
                      {analyzing ? (
                        <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing…</>
                      ) : (
                        <><Play className="h-4 w-4 mr-2" />Run Analysis</>
                      )}
                    </button>
                  </div>
                )}
                {analyzeError && (
                  <div className="mt-3 flex items-center gap-2 text-crimson text-sm">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {analyzeError}
                  </div>
                )}
              </div>
            )}


            {analysesLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-electric" />
              </div>
            ) : analysesError ? (
              <div className="bg-crimson/10 text-crimson p-4 rounded-lg flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span>{analysesError}</span>
              </div>
            ) : analyses.length === 0 ? (
              <div className="text-center p-8">
                <Brain className="h-12 w-12 text-slate/50 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-midnight mb-2">No Analysis Available</h3>
                <p className="text-slate mb-4">
                  {showArchived
                    ? 'No archived analyses found'
                    : 'Use the "Analyze Sermon" button in the sermon editor to get AI-powered insights and feedback.'}
                </p>
                {!showArchived && (
                  <Link to="/dashboard" className="inline-flex items-center text-electric hover:text-electric/80">
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Return to Dashboard
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {analyses.map((analysis, index) => {
                  const cardStyles = [
                    { bg: 'bg-gradient-to-br from-white to-blue-50/40', border: 'border-blue-200/50', accent: 'text-blue-600', shadow: 'hover:shadow-blue-200/30' },
                    { bg: 'bg-gradient-to-br from-white to-emerald-50/40', border: 'border-emerald-200/50', accent: 'text-emerald-600', shadow: 'hover:shadow-emerald-200/30' },
                    { bg: 'bg-gradient-to-br from-white to-purple-50/40', border: 'border-purple-200/50', accent: 'text-purple-600', shadow: 'hover:shadow-purple-200/30' },
                    { bg: 'bg-gradient-to-br from-white to-amber-50/40', border: 'border-amber-200/50', accent: 'text-amber-600', shadow: 'hover:shadow-amber-200/30' },
                  ];
                  const cardStyle = cardStyles[index % cardStyles.length];

                  return (
                    <div
                      key={analysis.id}
                      className={`${cardStyle.bg} rounded-xl shadow-lg border ${cardStyle.border} overflow-hidden hover:shadow-xl ${cardStyle.shadow} transition-all duration-300`}
                    >
                      <div
                        className="p-4 md:p-8 cursor-pointer hover:bg-white/40 transition-all duration-200"
                        onClick={() => toggleExpanded(analysis.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className={`${cardStyle.accent} bg-white/60 p-3 rounded-xl mr-4`}>
                              <Brain className="h-6 w-6" />
                            </div>
                            <div>
                              <h3 className="text-xl font-semibold text-midnight">{analysis.title}</h3>
                              <div className={`text-sm ${cardStyle.accent} mt-1`}>
                                {new Date(analysis.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric', month: 'long', day: 'numeric'
                                })}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleArchive(analysis.id, analysis.archived); }}
                              className="p-3 text-slate hover:text-electric rounded-xl bg-white/50 hover:bg-white/80 transition-all shadow-sm"
                              title={analysis.archived ? 'Unarchive' : 'Archive'}
                            >
                              {analysis.archived ? <RotateCcw className="h-5 w-5" /> : <Archive className="h-5 w-5" />}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteAnalysis(analysis.id); }}
                              className="p-3 text-slate hover:text-crimson rounded-xl bg-white/50 hover:bg-crimson/10 transition-all shadow-sm"
                              title="Delete"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                            <div className={`p-3 ${cardStyle.accent} bg-white/50 rounded-xl`}>
                              {expandedAnalyses.has(analysis.id) ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                            </div>
                          </div>
                        </div>
                      </div>
                      {expandedAnalyses.has(analysis.id) && (
                        <div className="px-8 pb-8 border-t border-white/30">
                          <div className="mt-6">
                            {renderHistoryContent(analysis.content)}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Result header */}
            <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-sand/20 p-6">
              <div className="flex items-center">
                <Brain className="h-5 w-5 text-midnight mr-2" />
                <h2 className="font-semibold text-midnight">Analysis Results</h2>
                {analysisData.sermonTitle && (
                  <span className="ml-4 text-sm text-slate">
                    for: <span className="font-medium text-midnight">{analysisData.sermonTitle}</span>
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4">
                {analysisData.sermonId && (
                  <Link
                    to={`/dashboard/editor/${analysisData.sermonId}`}
                    className="text-sm text-electric hover:text-electric/80 flex items-center"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Return to Editor
                  </Link>
                )}
                <button
                  onClick={handleSaveAnalysis}
                  disabled={loading}
                  className="flex items-center px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-5 w-5 mr-2" />Save Analysis</>}
                </button>
              </div>
            </div>

            {/* Structured cards or legacy HTML */}
            {typeof analysisData.content === 'object' ? (
              <AnalysisCards data={analysisData.content as SermonAnalysisJSON} />
            ) : (
              <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl shadow-lg border border-blue-200/50 p-8">
                <div
                  className="prose prose-slate max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizePlainHtml(analysisData.content) }}
                />
              </div>
            )}
          </div>
        )}
      </PremiumFeature>
    );
  };

  const handleRunLanguageAnalysis = async () => {
    if (!langSermonId) return;
    setLangError(null);
    setLangAnalyzing(true);
    try {
      const { data: sermon } = await supabase
        .from('sermons')
        .select('title, content, scripture')
        .eq('id', langSermonId)
        .single();
      if (!sermon?.content) { setLangError('This sermon has no content to analyze.'); return; }
      const result = await analyzeLanguage(sermon.content, sermon.scripture || undefined);
      if (result.success) {
        setLanguageData(result.content as LanguageAnalysisJSON);
        setLangSermonTitle(sermon.title);
        setLangSaved(false);
      } else {
        setLangError(result.error || 'Analysis failed');
      }
    } catch (err) {
      setLangError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLangAnalyzing(false);
    }
  };

  const handleSaveLangAnalysis = async () => {
    if (!languageData || langSaved) return;
    try {
      const saved = await saveLangAnalysis({
        sermon_id: langSermonId || null,
        title: langSermonTitle || languageData.scripture || 'Word Study',
        content: languageData,
      });
      if (saved) setExpandedLangAnalyses(prev => new Set(prev).add(saved.id));
      setLangSaved(true);
      setLangSaveNotification(true);
    } catch (err) {
      setLangError(err instanceof Error ? err.message : 'Failed to save word study');
    }
  };

  const toggleLangExpanded = (id: string) => {
    setExpandedLangAnalyses(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const confirmLangDelete = async () => {
    if (!langDeleteTarget) return;
    try {
      await deleteLangAnalysis(langDeleteTarget);
      setExpandedLangAnalyses(prev => { const n = new Set(prev); n.delete(langDeleteTarget); return n; });
    } catch (err) {
      setLangError(err instanceof Error ? err.message : 'Failed to delete word study');
    } finally {
      setLangDeleteTarget(null);
    }
  };

  const renderLanguageContent = () => {
    return (
      <PremiumFeature feature="sermon_analysis">
        {langAnalyzing ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-electric" />
            <p className="text-sm text-slate">Searching the Hebrew and Greek text…</p>
          </div>
        ) : !languageData ? (
          <div>
            <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
              <h2 className="text-xl font-semibold text-midnight">Hebrew & Greek Language Analysis</h2>
            </div>
            <div className="mb-6 p-5 bg-white border border-electric/20 rounded-xl shadow-sm">
              <h3 className="text-sm font-semibold text-midnight mb-3 flex items-center gap-2">
                <Languages className="h-4 w-4 text-electric" />Select a sermon to analyze
              </h3>
              {sermonsLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-electric" />
              ) : (
                <div className="space-y-3">
                  <select
                    value={langSermonId}
                    onChange={e => setLangSermonId(e.target.value)}
                    className="w-full px-3 py-2 border border-sand rounded-lg text-sm text-midnight bg-white focus:ring-2 focus:ring-electric focus:border-transparent"
                  >
                    <option value="">Choose a sermon…</option>
                    {sermonList.map(s => <option key={s.id} value={s.id}>{s.title}{s.scripture ? ` — ${s.scripture}` : ''}</option>)}
                  </select>
                  {langError && <p className="text-sm text-crimson">{langError}</p>}
                  <button
                    onClick={handleRunLanguageAnalysis}
                    disabled={!langSermonId || langAnalyzing}
                    className="flex items-center gap-2 px-4 py-2 bg-electric text-white text-sm font-medium rounded-lg hover:bg-electric/90 disabled:opacity-50 transition-colors"
                  >
                    {langAnalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
                    Run Language Analysis
                  </button>
                </div>
              )}
            </div>

            {/* Saved word studies */}
            {langAnalyses.length > 0 ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Languages className="h-4 w-4 text-amber-600" />
                  <h3 className="text-sm font-semibold text-midnight">Saved Word Studies</h3>
                  <span className="text-xs text-slate bg-sand/40 px-2 py-0.5 rounded-full">{langAnalyses.length}</span>
                </div>
                <div className="space-y-3">
                  {langAnalyses.map((analysis: LanguageAnalysisRecord) => (
                    <div key={analysis.id} className="bg-white rounded-xl border border-amber-200/60 overflow-hidden">
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-amber-50/40 transition-colors"
                        onClick={() => toggleLangExpanded(analysis.id)}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="shrink-0 p-2 bg-amber-100 rounded-lg">
                            <Languages className="h-4 w-4 text-amber-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-midnight truncate">{analysis.title}</p>
                            <p className="text-xs text-slate">{analysis.content.scripture} · {new Date(analysis.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={e => { e.stopPropagation(); setLangDeleteTarget(analysis.id); }}
                            className="p-2 text-slate hover:text-crimson rounded-lg hover:bg-crimson/10 transition-all"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          {expandedLangAnalyses.has(analysis.id) ? <ChevronUp className="h-4 w-4 text-slate" /> : <ChevronDown className="h-4 w-4 text-slate" />}
                        </div>
                      </div>
                      {expandedLangAnalyses.has(analysis.id) && (
                        <div className="border-t border-amber-100 p-5">
                          <LanguageAnalysisView data={analysis.content} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-14 bg-white rounded-xl border border-sand/20">
                <Languages className="h-12 w-12 text-slate/30 mx-auto mb-3" />
                <p className="text-slate text-sm max-w-sm mx-auto">Select a sermon above to surface key Hebrew and Greek terms from the passage — with Strong's references, lexical definitions, and preaching applications from a conservative evangelical lens.</p>
              </div>
            )}
          </div>
        ) : (
          <LanguageAnalysisView
            data={languageData}
            onReset={() => setLanguageData(null)}
            onSave={handleSaveLangAnalysis}
            isSaved={langSaved}
          />
        )}
      </PremiumFeature>
    );
  };

  const renderContent = () => {
    if (activeTool === 'language') return renderLanguageContent();
    if (activeTool === 'worship-song') return <WorshipSongAnalysis />;
    if (activeTool === 'speaker-vetting') return <SpeakerVetting />;
    return renderAnalysisContent();
  };

  const groupedTools = tools.reduce((acc, tool) => {
    if (!acc[tool.category]) acc[tool.category] = [];
    acc[tool.category].push(tool);
    return acc;
  }, {} as Record<string, typeof tools>);

  return (
    <div className="h-screen flex flex-col lg:flex-row bg-sand/10">
      <div className="research-sidebar lg:w-72 bg-white border-b lg:border-b-0 lg:border-r flex-shrink-0">
        {/* Desktop: title header */}
        <div className="hidden lg:block p-4 md:p-6 border-b">
          <h1 className="text-xl font-bold text-midnight">AI Analysis</h1>
        </div>
        {/* Mobile/tablet: horizontal scrolling tab strip */}
        <div className="lg:hidden flex overflow-x-auto no-scrollbar border-b py-2 px-3 gap-1">
          {tools.map((tool) => (
            <button
              key={tool.id}
              onClick={() => setActiveTool(tool.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg whitespace-nowrap flex-shrink-0 text-sm font-medium transition-colors ${
                activeTool === tool.id
                  ? 'bg-electric/10 text-electric'
                  : 'text-slate hover:bg-sand/20 dark:text-soft-white/70 dark:hover:bg-charcoal/20'
              }`}
            >
              <tool.icon className="h-4 w-4 flex-shrink-0" />
              {tool.name}
            </button>
          ))}
        </div>
        {/* Desktop: full vertical nav */}
        <nav className="hidden lg:block flex-1 overflow-y-auto p-4">
          {Object.entries(groupedTools).map(([category, categoryTools]) => (
            <div key={category} className="mb-6 last:mb-0">
              <h2 className="text-sm font-medium text-slate mb-2 px-2">{category}</h2>
              {categoryTools.map((tool) => (
                <button
                  key={tool.id}
                  onClick={() => setActiveTool(tool.id)}
                  className="research-tool-button"
                  data-active={activeTool === tool.id}
                >
                  <tool.icon className="research-tool-icon" />
                  <div className="flex-1">
                    <div className="research-tool-label">{tool.name}</div>
                    <div className="research-tool-description">{tool.description}</div>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </nav>
      </div>

      <div className="flex-1 overflow-auto p-4 md:p-8 pb-20 lg:pb-8">
        {showBanner && (
          <div className="mb-6 -mx-4 md:-mx-8">
            <CompactTrialBanner daysLeft={daysLeftInTrial} onDismiss={dismissBanner} />
          </div>
        )}
        {renderContent()}
      </div>

      <SaveNotification
        show={saveNotification}
        onHide={() => setSaveNotification(false)}
        message="Analysis saved successfully"
        type="success"
      />

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Analysis"
        message="Are you sure you want to permanently delete this analysis? This cannot be undone."
        confirmText="Delete"
        icon={<Trash2 className="h-5 w-5 text-crimson" />}
      />

      <SaveNotification
        show={langSaveNotification}
        onHide={() => setLangSaveNotification(false)}
        message="Word study saved"
        type="success"
      />

      <ConfirmationModal
        isOpen={langDeleteTarget !== null}
        onClose={() => setLangDeleteTarget(null)}
        onConfirm={confirmLangDelete}
        title="Delete Word Study"
        message="Are you sure you want to permanently delete this word study? This cannot be undone."
        confirmText="Delete"
        icon={<Trash2 className="h-5 w-5 text-crimson" />}
      />
    </div>
  );
}

// ─── Language Analysis UI ────────────────────────────────────────────────────

const LANGUAGE_COLORS: Record<string, { bg: string; border: string; badge: string }> = {
  Hebrew:  { bg: 'bg-amber-50',  border: 'border-amber-200',  badge: 'bg-amber-100 text-amber-800' },
  Greek:   { bg: 'bg-blue-50',   border: 'border-blue-200',   badge: 'bg-blue-100 text-blue-800' },
  Aramaic: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-800' },
};

function useCopy(resetMs = 2000) {
  const [copied, setCopied] = useState(false);
  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), resetMs);
  };
  return { copied, copy };
}

function CopyBtn({ text, className = '' }: { text: string; className?: string }) {
  const { copied, copy } = useCopy();
  return (
    <button
      onClick={e => { e.stopPropagation(); copy(text); }}
      title="Copy to clipboard"
      className={`flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-lg transition-colors ${
        copied ? 'bg-emerald/10 text-emerald' : 'bg-white/60 text-slate hover:bg-white hover:text-midnight'
      } ${className}`}
    >
      {copied ? <><Check className="h-3 w-3" />Copied</> : <><Copy className="h-3 w-3" />Copy</>}
    </button>
  );
}

function TermCard({ term }: { term: LanguageTermEntry }) {
  const [open, setOpen] = useState(true);
  const colors = LANGUAGE_COLORS[term.language] ?? LANGUAGE_COLORS.Greek;

  const fullTermText = [
    `${term.word} (${term.transliteration}) — ${term.strongsNumber} · ${term.language} ${term.partOfSpeech}`,
    `Literal: ${term.literalMeaning}`,
    ``,
    `Theological Significance: ${term.theologicalSignificance}`,
    ``,
    `Preaching Application: ${term.preachingApplication}`,
  ].join('\n');

  return (
    <div className={`rounded-xl border ${colors.border} ${colors.bg} overflow-hidden`}>
      {/* Header */}
      <div className="flex items-start p-5 gap-2">
        <button
          onClick={() => setOpen(v => !v)}
          className="flex-1 flex items-start gap-4 min-w-0 text-left"
        >
          <div className="shrink-0 text-center">
            <div className="text-3xl font-serif leading-none mb-1">{term.word}</div>
            <div className="text-xs font-mono text-slate">{term.transliteration}</div>
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.badge}`}>{term.language}</span>
              <span className="text-xs text-slate bg-white/70 px-2 py-0.5 rounded-full border border-sand/30">{term.strongsNumber}</span>
              <span className="text-xs text-slate/70 italic">{term.partOfSpeech}</span>
            </div>
            <p className="text-sm font-semibold text-midnight truncate">{term.literalMeaning}</p>
            <p className="text-xs text-slate/70 mt-0.5">{term.passageContext}</p>
            {term.scriptureReferences && (
              <p className="text-xs font-mono text-electric/80 mt-0.5">{term.scriptureReferences}</p>
            )}
          </div>
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          <CopyBtn text={fullTermText} />
          <button onClick={() => setOpen(v => !v)}>
            {open ? <ChevronUp className="h-4 w-4 text-slate" /> : <ChevronDown className="h-4 w-4 text-slate" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-sand/20 divide-y divide-sand/10">
          <TermSection icon={<ScrollText className="h-3.5 w-3.5" />} label="Root & Etymology" copyText={term.rootMeaning}>
            {term.rootMeaning}
          </TermSection>
          <TermSection icon={<BookOpen className="h-3.5 w-3.5" />} label="Theological Significance" copyText={term.theologicalSignificance}>
            {term.theologicalSignificance}
          </TermSection>
          <TermSection icon={<Shield className="h-3.5 w-3.5" />} label="Conservative Lexical Note" copyText={term.conservativeNotes}>
            {term.conservativeNotes}
          </TermSection>
          <TermSection icon={<Mic2 className="h-3.5 w-3.5" />} label="Preaching Application" copyText={term.preachingApplication} highlight>
            {term.preachingApplication}
          </TermSection>
        </div>
      )}
    </div>
  );
}

function TermSection({ icon, label, children, copyText, highlight }: {
  icon: React.ReactNode; label: string; children: string; copyText?: string; highlight?: boolean;
}) {
  return (
    <div className={`px-5 py-3.5 ${highlight ? 'bg-electric/5' : ''}`}>
      <div className={`flex items-center justify-between gap-2 mb-1.5`}>
        <div className={`flex items-center gap-1.5 ${highlight ? 'text-electric' : 'text-slate'}`}>
          {icon}
          <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
        </div>
        {copyText && <CopyBtn text={copyText} />}
      </div>
      <p className="text-sm text-midnight leading-relaxed">{children}</p>
    </div>
  );
}

function LanguageAnalysisView({ data, onReset, onSave, isSaved }: {
  data: LanguageAnalysisJSON;
  onReset?: () => void;
  onSave?: () => void;
  isSaved?: boolean;
}) {
  return (
    <div>
      {(onReset || onSave) && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-xl font-semibold text-midnight">Hebrew & Greek Language Analysis</h2>
            {data.scripture && (
              <p className="text-sm text-slate mt-0.5 flex items-center gap-1.5">
                <BookOpen className="h-3.5 w-3.5" />{data.scripture}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onSave && (
              <button
                onClick={onSave}
                disabled={isSaved}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-electric text-white hover:bg-electric/90 disabled:opacity-50 transition-colors text-sm"
              >
                {isSaved ? <><Check className="h-4 w-4" />Saved</> : <><Save className="h-4 w-4" />Save Word Study</>}
              </button>
            )}
            {onReset && (
              <button
                onClick={onReset}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-sand/20 text-slate hover:bg-sand/40 transition-colors text-sm"
              >
                <RotateCcw className="h-4 w-4" />New Analysis
              </button>
            )}
          </div>
        </div>
      )}

      {data.exegeticalSummary && (
        <div className="rounded-xl border border-electric/20 bg-electric/5 p-5 mb-6">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-electric" />
              <span className="text-xs font-bold uppercase tracking-widest text-electric">Exegetical Summary</span>
            </div>
            <CopyBtn text={data.exegeticalSummary} />
          </div>
          <p className="text-sm text-midnight leading-relaxed">{data.exegeticalSummary}</p>
        </div>
      )}

      <div className="space-y-4">
        {data.terms.map((term, i) => <TermCard key={i} term={term} />)}
      </div>

      <p className="text-xs text-slate/50 mt-6 text-center">
        Analysis uses BDB (Hebrew) and BDAG (Greek) as primary lexical authorities within a conservative evangelical framework.
      </p>
    </div>
  );
}
